// Edit-patient modal — slim version of v2's openPatientModalFull, scoped to
// fields meaningful for onboarding readiness. Fields explicitly omitted:
//   - billing programs (set during Enroll on the parent surface)
//   - psych / pain / PCM / RPM / nav programs (out of scope for Onboard)
//   - consent state (set during Enroll)
//   - MRN, DOB, sex (synced from Elation, read-only here)
//
// To edit the omitted fields, users still have /v2's full editor.

import React, { useState, useEffect } from 'react';
import { useUnsavedChangesGuard } from '../lib/hooks/use-unsaved-changes-guard';

export function EditPatientModal({ patient, busy, onClose, onSave }) {
  const raw = patient.raw || {};
  const [fname, setFname] = useState(raw.fname || '');
  const [lname, setLname] = useState(raw.lname || '');
  const [provider, setProvider] = useState(raw.provider || '');
  const [notes, setNotes] = useState(raw.notes || '');
  const [tagsText, setTagsText] = useState((raw.tags || []).join(', '));
  const [conditions, setConditions] = useState(
    (raw.conditions || []).map((c) => ({
      description: c.description || '',
      icd10: Array.isArray(c.icd10) ? c.icd10.join(', ') : (c.icd10 || ''),
      conditionId: c.conditionId || '',
    }))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const { attemptDismiss, showDiscardPrompt, confirmDiscard, keepEditing } =
    useUnsavedChangesGuard({
      values: { fname, lname, provider, notes, tagsText, conditions },
      onClose,
      busy: saving || busy,
    });

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') attemptDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [attemptDismiss]);

  function updateCondition(i, key, val) {
    setConditions((cs) => cs.map((c, idx) => idx === i ? { ...c, [key]: val } : c));
  }
  function addCondition() {
    setConditions((cs) => [...cs, { description: '', icd10: '', conditionId: '' }]);
  }
  function removeCondition(i) {
    setConditions((cs) => cs.filter((_, idx) => idx !== i));
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const next = { ...raw };
      next.fname = fname.trim();
      next.lname = lname.trim();
      next.provider = provider.trim();
      next.notes = notes;
      next.tags = tagsText.split(',').map((s) => s.trim()).filter(Boolean);
      next.conditions = conditions
        .filter((c) => c.description.trim())
        .map((c) => ({
          description: c.description.trim(),
          icd10: c.icd10.split(',').map((s) => s.trim()).filter(Boolean),
          conditionId: c.conditionId || undefined,
        }));
      await onSave(next);
      onClose();
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={attemptDismiss}>
      <div
        className="modal edit-patient-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Edit patient"
        onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Edit patient</h3>
          <button className="btn ghost icon" onClick={attemptDismiss} aria-label="Close" disabled={saving || busy}>✕</button>
        </div>
        {showDiscardPrompt ? (
          <>
            <div className="modal-body">
              <h3 style={{ marginTop: 0 }}>Discard changes?</h3>
              <p>You've made changes but haven't saved yet.</p>
            </div>
            <div className="modal-foot">
              <button className="btn" onClick={keepEditing}>Keep editing</button>
              <button className="btn warn" onClick={confirmDiscard}>Discard</button>
            </div>
          </>
        ) : (
          <>
            <div className="modal-body">
              <div className="form-row">
                <label>First name<input value={fname} onChange={(e) => setFname(e.target.value)} disabled={saving} /></label>
                <label>Last name<input value={lname} onChange={(e) => setLname(e.target.value)} disabled={saving} /></label>
              </div>
              <div className="form-row readonly">
                <span>MRN: <b>{raw.mrn || '—'}</b></span>
                <span>DOB: <b>{raw.dob || '—'}</b></span>
                <span>Sex: <b>{raw.sex || '—'}</b></span>
              </div>
              <label>Provider<input value={provider} onChange={(e) => setProvider(e.target.value)} disabled={saving} /></label>
              <label>Tags (comma-separated)<input value={tagsText} onChange={(e) => setTagsText(e.target.value)} disabled={saving} /></label>
              <fieldset className="conditions-fieldset">
                <legend>Conditions</legend>
                {conditions.length === 0 && <div className="empty">No conditions documented.</div>}
                {conditions.map((c, i) => (
                  <div className="condition-row" key={i}>
                    <input
                      placeholder="Description (e.g. Type 2 diabetes)"
                      value={c.description}
                      onChange={(e) => updateCondition(i, 'description', e.target.value)}
                      disabled={saving}
                    />
                    <input
                      placeholder="ICD-10 (e.g. E11.9)"
                      value={c.icd10}
                      onChange={(e) => updateCondition(i, 'icd10', e.target.value)}
                      disabled={saving}
                      style={{ width: 140 }}
                    />
                    <button className="btn ghost sm" onClick={() => removeCondition(i)} disabled={saving}>✕</button>
                  </div>
                ))}
                <button className="btn sm" onClick={addCondition} disabled={saving}>+ Add condition</button>
              </fieldset>
              <label>Notes<textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={saving} /></label>
              {error && <div className="form-error">{error}</div>}
            </div>
            <div className="modal-foot">
              <button className="btn" onClick={attemptDismiss} disabled={saving || busy}>Cancel</button>
              <button className="btn primary" onClick={save} disabled={saving || busy}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
